import { css } from '@emotion/react';
import 'swiper/css';
import 'swiper/css/pagination';

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import Button from 'components/field/Button';
import SwitchInput from 'components/field/SwitchInput';
import { Form } from 'components/form/FormLayout.styled';
import Accordion from 'components/global/Accordion';
import Icon from 'components/global/Icon';
import Typography from 'components/global/Typography';
import { ModalActions } from 'components/global/styled/Modal.styled';

import useAlert from 'hooks/useAlert';
import useLocale from 'hooks/useLocale';
import useMediaQuery from 'hooks/useMediaQuery';

import { getOTOPackageList } from 'pages/OTOModules/OTOPackageListSlice';

import { Breakpoints } from 'utils/const.styles';

import { faBadgeCheck, faCircleCheck } from '@fortawesome/pro-solid-svg-icons';

import * as S from '../SubscriptonAccountModal.styled';

export default function PackageSelectionModal({
	closeModal,
	nextStep,
	selectedPackage,
	currentPackage,
	setSelectedPackage,
	planType,
	setPlanType
}) {
	const { t } = useTranslation();
	const isMobile = useMediaQuery(Breakpoints.mobile);
	const dispatch = useDispatch();
	const { showInfo } = useAlert();
	const { getLocaleDateWithTime } = useLocale();
	useEffect(() => {
		dispatch(getOTOPackageList());
	}, []);

	const user = useSelector((state) => state.user);
	const { otoPackageList: packages } = useSelector((state) => state?.otoPackageList);
	const changePackage = (packageObj, planName) => {
		if (user.nextPackageName !== user.planName && planName !== 'EnterprisePackage') {
			showInfo({
				header: 'infoMsg.requestedPackageAlertHeader',
				message: t('infoMsg.requestedPackageAlertMessage', {
					currentPlanExpiryDate: getLocaleDateWithTime(user.currentPlanExpiryDate)
				})
			});
			return;
		}
		setSelectedPackage(packageObj);
	};

	return (
		<>
			<S.ModalContentWrapper>
				<Form style={{ border: '0', justifyContent: 'center' }}>
					<div
						style={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center'
						}}
						className="pb-5 pt-4">
						<Typography
							variant="p1"
							className="px-3"
							fontWeight={planType === 'monthly' ? 'bold' : 'normal'}>
							monthly
						</Typography>

						<SwitchInput
							value={planType === 'yearly'}
							setValue={() => {
								setPlanType(planType === 'yearly' ? 'monthly' : 'yearly');
							}}
						/>

						<Typography
							variant="p1"
							className="px-3"
							fontWeight={planType === 'yearly' ? 'bold' : 'normal'}>
							yearly
						</Typography>

						<S.DiscountContainer>
							<Icon icon={faBadgeCheck} color="rgb(58, 182, 116)" />
							<Typography fontWeight="700" color="rgb(58, 182, 116)">
								{t('upgradeAccount.save')} 25%
							</Typography>
						</S.DiscountContainer>
					</div>
					{!isMobile && (
						<div
							style={{ width: '100%' }}
							className="row row-cols-1 row-cols-lg-4 row-cols-md-2 row-cols-sm-1">
							{packages.map((packageObj, index) => {
								const {
									tier,
									planName,
									monthlyPrice,
									yearlyPrice,
									featureExplainText,
									yearlyPriceWithoutDiscount,
									monthlyPriceWithoutDiscount,
									properties,
									pricePlaceholder,
									currency,
									monthlyFreeIntegratorOrder,
									extraOrderCost
								} = packageObj;

								return (
									<S.CardContainer key={index}>
										<S.UpgradeAccountPackage
											className={`${user.subscriptionPaymentType === planType.toUpperCase() && selectedPackage.tier === tier ? 'active' : ''}`}
											onClick={() => changePackage(packageObj, planName)}>
											<div style={{ display: 'flex', justifyContent: 'space-between' }}>
												<Typography
													fontWeight="700"
													style={{ alignSelf: 'self-end' }}
													color={selectedPackage.tier === tier ? 'var(--blue)' : ''}>
													{`${planName}.packageTitle`}
												</Typography>

												<S.Switch key={index} value={tier === selectedPackage.tier} />
											</div>

											{(user.subscriptionPaymentType === planType.toUpperCase() ||
												user.planName === 'FreePackage') &&
												currentPackage.tier === tier && (
													<S.CurrentPlanStyle
														className={`${
															selectedPackage.tier === tier
																? 'activeBg mt-2'
																: 'passiveBg mt-2'
														}`}>
														<Typography
															variant="caption"
															fontWeight="500"
															color="var(--white)">
															currentPlan
														</Typography>
													</S.CurrentPlanStyle>
												)}

											<div style={{ position: 'relative' }} className="mt-5">
												<div className="priceMargin">
													<S.PriceContainerPosition>
														<Typography
															style={{
																textDecoration: 'line-through',
																alignSelf: 'center'
															}}
															color="var(--content-3)"
															fontWeight="500">
															{planType === 'yearly'
																? yearlyPriceWithoutDiscount
																	? (yearlyPriceWithoutDiscount / 12)?.toFixed(0)
																	: yearlyPriceWithoutDiscount?.toFixed(0)
																: monthlyPriceWithoutDiscount}{' '}
															{planType === 'yearly' &&
																yearlyPriceWithoutDiscount?.toFixed(0) &&
																currency}
															{planType === 'monthly' &&
																monthlyPriceWithoutDiscount &&
																currency}
														</Typography>
													</S.PriceContainerPosition>

													<div className="mb-5">
														<div style={{ display: 'flex' }}>
															<Typography variant="p1" fontWeight="bold">
																{pricePlaceholder
																	? t(pricePlaceholder)
																	: planType === 'yearly'
																		? yearlyPrice
																			? (yearlyPrice / 12)?.toFixed(0)
																			: yearlyPrice === 0
																				? 0
																				: yearlyPrice?.toFixed(0)
																		: monthlyPrice}{' '}
																{planName !== 'EnterprisePackage' ? currency : ' '}
															</Typography>
															{planName !== 'EnterprisePackage' && (
																<Typography
																	style={{ alignSelf: 'center' }}
																	variant="p2"
																	color="var(--content-3)"
																	fontWeight="bold"
																	className="mt-1">
																	/{t('month')}
																</Typography>
															)}
														</div>
													</div>
												</div>
											</div>

											{planName !== 'EnterprisePackage' && (
												<div className="mb-5">
													<S.InlineTypography>
														<Typography variant="caption" fontWeight="bold" noWrap>
															{monthlyFreeIntegratorOrder} {t('freeShipment')}
														</Typography>
														<Typography
															variant="caption"
															color="var(--content-3)"
															noWrap
															className="mx-1">
															/
														</Typography>
														<Typography variant="caption" color="var(--content-3)" noWrap>
															upgradeAccountPage.monthlyYourOwnRates
														</Typography>
													</S.InlineTypography>
													<S.InlineTypography className="mt-2">
														<Typography variant="caption" fontWeight="bold" noWrap>
															{extraOrderCost} {t(currency)}
														</Typography>
														<Typography variant="caption" color="var(--content-3)" noWrap>
															/{t('extraShipment')}
														</Typography>
													</S.InlineTypography>
												</div>
											)}
											<Typography fontWeight="500" className="mb-3" noWrap>
												{featureExplainText}
											</Typography>
											<ul>
												{properties.map((item) => (
													<li key={item} style={{ display: 'flex' }}>
														<span>
															<Icon icon={faCircleCheck} style={{ color: '#3AB674' }} />
														</span>
														<Typography noWrap>{item}</Typography>
													</li>
												))}
											</ul>
										</S.UpgradeAccountPackage>
									</S.CardContainer>
								);
							})}
						</div>
					)}

					{isMobile && (
						<S.MobileContainer
							style={{ width: '100%' }}
							className="row row-cols-1 row-cols-lg-4 row-cols-md-2 row-cols-sm-1">
							{packages.map((packageObj, index) => {
								const {
									tier,
									planName,
									monthlyPrice,
									yearlyPrice,
									featureExplainText,
									yearlyPriceWithoutDiscount,
									monthlyPriceWithoutDiscount,
									properties,
									pricePlaceholder,
									currency,
									monthlyFreeIntegratorOrder,
									extraOrderCost
								} = packageObj;

								return (
									<S.CardContainer key={index} onClick={() => changePackage(packageObj, planName)}>
										<S.UpgradeAccountPackage
											className={`${selectedPackage.tier === tier ? 'active' : ''}`}>
											<div style={{ display: 'flex', justifyContent: 'space-between' }}>
												<Typography
													fontWeight="700"
													style={{ alignSelf: 'self-end' }}
													color={selectedPackage.tier === tier ? 'var(--blue)' : ''}>
													{`${planName}.packageTitle`}
												</Typography>

												<S.Switch key={index} value={tier === selectedPackage.tier} />
											</div>

											{currentPackage.tier === tier && (
												<S.CurrentPlanStyle
													className={`${
														selectedPackage.tier === tier
															? 'activeBg mt-2'
															: 'passiveBg mt-2'
													}`}>
													<Typography variant="caption" fontWeight="500" color="var(--white)">
														currentPlan
													</Typography>
												</S.CurrentPlanStyle>
											)}

											<div style={{ position: 'relative' }} className="mt-1">
												<div className="mt-5">
													<div>
														<Typography
															style={{
																textDecoration: 'line-through',
																alignSelf: 'center'
															}}
															color="var(--content-3)"
															fontWeight="500">
															{planType === 'yearly'
																? yearlyPriceWithoutDiscount
																	? (yearlyPriceWithoutDiscount / 12)?.toFixed(0)
																	: yearlyPriceWithoutDiscount?.toFixed(0)
																: monthlyPriceWithoutDiscount}{' '}
															{planType === 'yearly' &&
																yearlyPriceWithoutDiscount?.toFixed(0) &&
																currency}
															{planType === 'monthly' &&
																monthlyPriceWithoutDiscount &&
																currency}
														</Typography>
													</div>

													<div>
														<div style={{ display: 'flex' }}>
															<Typography variant="p1" fontWeight="bold">
																{pricePlaceholder
																	? t(pricePlaceholder)
																	: planType === 'yearly'
																		? yearlyPrice
																			? (yearlyPrice / 12)?.toFixed(0)
																			: yearlyPrice === 0
																				? 0
																				: yearlyPrice?.toFixed(0)
																		: monthlyPrice}{' '}
																{planName !== 'EnterprisePackage' ? currency : ' '}
															</Typography>
															{planName !== 'EnterprisePackage' && (
																<Typography
																	style={{ alignSelf: 'center' }}
																	variant="p2"
																	color="var(--content-3)"
																	fontWeight="bold"
																	className="mt-1">
																	/{t('month')}
																</Typography>
															)}
														</div>
													</div>
												</div>
											</div>

											{planName !== 'EnterprisePackage' && (
												<div className="my-3">
													<S.InlineTypography>
														<Typography variant="caption" fontWeight="bold" noWrap>
															{monthlyFreeIntegratorOrder} {t('freeShipment')}
														</Typography>
														<Typography
															variant="caption"
															color="var(--content-3)"
															noWrap
															className="mx-1">
															/
														</Typography>
														<Typography variant="caption" color="var(--content-3)" noWrap>
															upgradeAccountPage.monthlyYourOwnRates
														</Typography>
													</S.InlineTypography>
													<S.InlineTypography className="mt-2">
														<Typography variant="caption" fontWeight="bold" noWrap>
															{extraOrderCost} {t(currency)}
														</Typography>
														<Typography variant="caption" color="var(--content-3)" noWrap>
															/{t('extraShipment')}
														</Typography>
													</S.InlineTypography>
												</div>
											)}

											<Accordion
												header={featureExplainText}
												className="mt-4"
												headerStyle={{ padding: '1rem' }}>
												<ul>
													{properties.map((item) => (
														<li key={item} style={{ display: 'flex' }}>
															<span>
																<Icon
																	icon={faCircleCheck}
																	style={{ color: '#3AB674' }}
																/>
															</span>
															<Typography noWrap>{item}</Typography>
														</li>
													))}
												</ul>
											</Accordion>
										</S.UpgradeAccountPackage>
									</S.CardContainer>
								);
							})}
						</S.MobileContainer>
					)}
				</Form>
			</S.ModalContentWrapper>
			<ModalActions className="subscription-modal-actions">
				<Button
					variant="blackOutline"
					css={css`
						text-decoration: underline;
						border: none !important;
					`}
					label="cancel"
					onClick={closeModal}
				/>

				<Button
					variant="blue"
					disabled={
						(user.subscriptionPaymentType === planType.toUpperCase() || user.planName === 'FreePackage') &&
						!user.accountSuspended &&
						(currentPackage.tier === selectedPackage.tier ||
							user.nextPackageName === selectedPackage.planName)
					}
					disabledTooltip={
						(currentPackage.tier === selectedPackage.tier ||
							user.nextPackageName === selectedPackage.planName) &&
						'formInfo.pleaseSelectAnotherPackage'
					}
					label="continue"
					onClick={() => {
						nextStep();
					}}
				/>
			</ModalActions>
		</>
	);
}
